import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { map, first } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { APIRoutes } from '../APIRoutes';
import { AppConstants } from '../AppConstants';
import { of } from 'rxjs';
import { ProductLandingComponent } from '../product-landing/product-landing.component';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
// import { DatePipe } from '@angular/common';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private jwtService: JwtHelperService;
  private persistentSessionKeys = [
    AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT,
    AppConstants.SESSION_STORAGE_ACCESS_TOKEN,
    AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT,
    'selected-tenant-id'
  ];
  // datePipe: DatePipe = new DatePipe('en-US');
  constructor(private httpClient: HttpClient ) {
    this.jwtService = new JwtHelperService();
   }

  getUsername() {
    const userContext = this.getStoredItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT)
      || this.getStoredItem(AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT);
    const user = userContext ? JSON.parse(userContext) : null;
    return user ? (user.username || user.email) : null;
  }

  syncUserContext() {
    // Existing admin screens read sessionStorage directly after a browser restart.
    [
      AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT,
      AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT,
      AppConstants.SESSION_STORAGE_ACCESS_TOKEN,
      'selected-tenant-id'
    ].forEach(key => {
      const value = this.getStoredItem(key);
      if (value !== null) {
        try {
          sessionStorage.setItem(key, value);
        } catch (error) {
          console.warn('Unable to restore sessionStorage', error);
        }
      }
    });
  this.restorePersistentLogin();
    const currentUserContext = this.getStoredItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT);
    const externalUserContext = this.getStoredItem(AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT);
    const selectedTenantId = this.getStoredItem('selected-tenant-id');
    const userContext = currentUserContext || externalUserContext;

    if (!userContext) {
      AppConstants.SESSION_STORAGE_ROLEID = 0;
      AppConstants.SESSION_STORAGE_TENANTID = 0;
      return null;
    }

    const parsedContext = JSON.parse(userContext);
    AppConstants.SESSION_STORAGE_ROLEID = parsedContext.roleId || 0;
    AppConstants.SESSION_STORAGE_TENANTID = selectedTenantId
      ? Number(selectedTenantId)
      : (parsedContext.tenantId || 0);

    return parsedContext;
  }

  restorePersistentLogin(): void {
    this.persistentSessionKeys.forEach(key => {
      const sessionValue = sessionStorage.getItem(key);
      const persistentValue = localStorage.getItem(key);

      if (!sessionValue && persistentValue) {
        sessionStorage.setItem(key, persistentValue);
      }
    });
  }

  async initializePersistentLogin(): Promise<void> {
    this.restorePersistentLogin();
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    for (const key of this.persistentSessionKeys) {
      const sessionValue = sessionStorage.getItem(key);

      if (sessionValue) {
        localStorage.setItem(key, sessionValue);
        continue;
      }

      const localValue = localStorage.getItem(key);

      if (localValue) {
        sessionStorage.setItem(key, localValue);
        continue;
      }

      const preference = await Preferences.get({ key });

      if (preference.value) {
        sessionStorage.setItem(key, preference.value);
        localStorage.setItem(key, preference.value);
      }
    }
  }

  persistLogin(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.persistentSessionKeys.forEach(key => {
      const value = sessionStorage.getItem(key);

      if (value) {
        localStorage.setItem(key, value);
        Preferences.set({ key, value }).catch(() => undefined);
      } else {
        localStorage.removeItem(key);
        Preferences.remove({ key }).catch(() => undefined);
      }
    });
  }

  clearLogin(): void {
    sessionStorage.clear();
    this.clearStoredAuth();

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.persistentSessionKeys.forEach(key => localStorage.removeItem(key));
    this.persistentSessionKeys.forEach(key => {
      Preferences.remove({ key }).catch(() => undefined);
    });
  }

  shouldValidateTokenExpiry(): boolean {
    return !Capacitor.isNativePlatform();
  }

  getTokenExpiryTime() {
    const userContext = this.syncUserContext();
    const expiryTime = userContext ? userContext.exp : 0;
    const currentTime = Date.now() / 1000;
    return (expiryTime - currentTime);
  }

  login(username: string, password: string) {
    return this.httpClient.post<any>(APIRoutes.AUTH_TOKEN_URI, {username, password})
    .pipe(map(user => {
      //console.log("User:auth service ", user);

      if(user && user.accessToken) {
        const decodedToken = this.jwtService.decodeToken(user.accessToken);
        this.setStoredItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT, JSON.stringify(decodedToken));
        this.setStoredItem(AppConstants.SESSION_STORAGE_ACCESS_TOKEN, JSON.stringify(user.accessToken));
        this.removeStoredItem('selected-tenant-id');
        this.syncUserContext();
        this.persistLogin();
      }
      console.log("Logging In");
      return user;
    }));
  }

  loginWithSocialUser(socialUser: any) {
    return this.httpClient.post<any>(APIRoutes.SOCIAL_AUTH_TOKEN_URI, {
                                              "email": socialUser.email,
                                              "firstName": socialUser.firstName,
                                              "lastName": socialUser.lastName,
                                              "socialToken": socialUser.idToken,
                                              "socialType": socialUser.provider,
      }).pipe(map(
          user => {
            console.log("User:auth service ", user);
            if(user && user.accessToken) {
              const decodedToken = this.jwtService.decodeToken(user.accessToken);
              this.setStoredItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT, JSON.stringify(decodedToken));
              this.setStoredItem(AppConstants.SESSION_STORAGE_ACCESS_TOKEN, JSON.stringify(user.accessToken));
              this.removeStoredItem('selected-tenant-id');
              this.syncUserContext();
              this.persistLogin();
            }
            return user;
          }
        ));
  }


  isLoggedIn(): boolean {
    this.restorePersistentLogin();

    return !!(
      this.getStoredItem(
        AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT
      ) ||
      this.getStoredItem(
        AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT
      )
    );
  }

  getStoredItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (error) {
      try {
        return localStorage.getItem(key);
      } catch (fallbackError) {
        return null;
      }
    }
  }

  setStoredItem(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn('Unable to write sessionStorage', error);
    }

    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Unable to write localStorage', error);
    }
  }

  removeStoredItem(key: string) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Unable to remove sessionStorage item', error);
    }

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Unable to remove localStorage item', error);
    }
  }

  clearStoredAuth() {
    [
      AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT,
      AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT,
      AppConstants.SESSION_STORAGE_ACCESS_TOKEN,
      AppConstants.SESSION_STORAGE_PAYMENT_SOURCE,
      'selected-tenant-id',
      'postLoginUrl'
    ].forEach(key => {
      this.removeStoredItem(key);

      if (Capacitor.isNativePlatform()) {
        Preferences.remove({ key }).catch(() => undefined);
      }
    });
  }

  sendOTP(username: string, type: string) {
    return this.httpClient.post(APIRoutes.SEND_OTP_URI, null, {params: {id: username, type: type}});
  }

  validateOTP(username: string, OTP: string, type: string) {
    return this.httpClient.post(APIRoutes.VALIDATE_OTP_URI, {username: username, OTP: OTP}, {params: {type: type}});
  }

  createExternalUser(firstname: string, lastname: string, username: string, roleId: number, phoneNumber: string, interestedIn: string) {
    return this.httpClient.post(APIRoutes.CREATE_EXTERNAL_USER_URI, {firstName: firstname, lastName: lastname, email: username, roleId: roleId, phoneNumber: phoneNumber, interestedIn: interestedIn});
  }

  resetPasswordAndActivateUser(userId: number, password: string, reEnterPassword: string) {
    return this.httpClient.post(APIRoutes.USER_ACTIVATION_URI + '/' + userId, {password: password, reenterPassword: reEnterPassword});
  }

  resetPassword(password: string, reEnterPassword: string) {
    return this.httpClient.post(APIRoutes.RESET_PASSWORD_URI, {password: password, reenterPassword: reEnterPassword});
  }

  emailPasswordResetLink(username: string) {
    return this.httpClient.post(APIRoutes.EMAIL_PASSWORD_RESET_LINK_URI, null, {params: {username: username}});
  }

  getFacilityDetails() {
    return this.httpClient.get(APIRoutes.FACILITY_TYPE_URI);
  }

  createTenantV2(facilityDetails) {
    return this.httpClient.post(APIRoutes.CREATE_TENANT_V2_URI, facilityDetails);
  }

  updateTenantByTenantId(tenantId: number, facilityDetails) {
    return this.httpClient.put(APIRoutes.UPDATE_TENANT_DETAILS_BY_TENANTID + '/' + tenantId, facilityDetails);
  }

  updateTenantStatusByTenantId(tenantId: number, status: string) {
    return this.httpClient.patch(APIRoutes.UPDATE_TENANT_STATUS_BY_TENANTID + tenantId, { status: status });
  }

  updateGoogleMapsLinkByTenantId(tenantId: number, googleMapsLink: string) {
    // let params = new HttpParams();
    // params.append("googleMapsLink", googleMapsLink);
    return this.httpClient.patch(APIRoutes.UPDATE_GOOGLE_MAPS_LINK_BY_TENANTID + tenantId, null, {params : {googleMapsLink: googleMapsLink}});
  }

  getTenantDetailsBYTenantID(tenantId) {
    return this.httpClient.get(APIRoutes.GET_TENANT_DETAIS_BY_TENANTID + tenantId);
  }

  getTenantHolidaysByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.GET_TENANT_HOLIDAYS_BY_TENANTID + '/' + tenantId);
  }

  getTenantAmenitiesByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.GET_TENANT_AMENITIES_BY_TENANTID + '/' + tenantId);
  }

  getTenantRulesByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.GET_TENANT_RULES_BY_TENANTID + '/' + tenantId);
  }

  updateTenantRules(request, tenantId) {
    return this.httpClient.put(APIRoutes.UPDATE_TENANT_RULE_BY_TENANTID + '/' + tenantId, request);
  }

  createNewAmenity(tenantId: number, amenityName: string) {
    return this.httpClient.post(APIRoutes.CREATE_TENANT_AMENITY, {tenantId: tenantId, amenityName: amenityName});
  }

  deleteAmenityByAmenityId(amenityId) {
    return this.httpClient.delete(APIRoutes.DELETE_TENANT_AMENITY + '/' + amenityId);
  }

  createNewHoliday(tenantId: number, holiday: string) {
    return this.httpClient.post(APIRoutes.CREATE_TENANT_HOLIDAY_BY_TENANTID, {tenantId: tenantId, date: holiday});
  }

  deleteHolidayByHolidayId(holidayId) {
    return this.httpClient.delete(APIRoutes.DELETE_TENANT_HOLIDAY_BY_HOLIDAYID + '/' + holidayId);
  }

  createNewRule(tenantId: number, ruleType: string, ruleIndex: number, description: string) {
    return this.httpClient.post(APIRoutes.CREATE_TENANT_RULE_BY_TENANTID, {tenantId: tenantId, ruleType: ruleType, ruleIndex: ruleIndex, description: description});
  }

  deleteRuleByRuleId(ruleId: number) {
    return this.httpClient.delete(APIRoutes.DELETE_TENANT_RULE_BY_RULEID + '/' + ruleId);
  }

  createProduct(product) {
    return this.httpClient.post(APIRoutes.CREATE_PRODUCT,
      {
        productName: product.productName,
        price: product.price,
        description: product.description,
        durationPerSlot: product.durationPerSlot,
        capacityPerVenue: product.capacityPerVenue != null ? product.capacityPerVenue : null,
        productType: product.productType,
        tenantId: product.tenantId,
        status: product.status,
        sessionType: product.sessionType != null ? product.sessionType : null,
        noOfSessions: product.noOfSessions != null ? product.noOfSessions : null,
        monthlyMultiBookingOption: product.monthlyMultiBookingOption != '' ? product.monthlyMultiBookingOption : null,
        monthlyMultiBookingSelectedDates: product.monthlyMultiBookingSelectedDates != '' ? product.monthlyMultiBookingSelectedDates : null,
        serviceType: product.serviceType,
        allowBooking: product.allowBooking,
        isCoaching: product.isCoaching,
        bookingWindowType: product.bookingWindowType != null ? product.bookingWindowType : null,
        bookingWindowValue: product.bookingWindowValue != null ? product.bookingWindowValue : null
      });
  }

  updateProduct(id, product) {
    return this.httpClient.put(APIRoutes.UPDATE_PRODUCT + id,
      {
        productName: product.productName,
        price: product.price,
        description: product.description,
        durationPerSlot: product.durationPerSlot,
        capacityPerVenue: product.capacityPerVenue,
        status: product.status,
        noOfSessions: Number(product.noOfSessions),
        monthlyMultiBookingSelectedDates: product.monthlyMultiBookingSelectedDates,
        allowBooking: product.allowBooking,
        isCoaching: product.isCoaching,
        serviceType: product.serviceType,
        bookingWindowType: product.bookingWindowType != null ? product.bookingWindowType : null,
        bookingWindowValue: product.bookingWindowValue != null ? product.bookingWindowValue : null
      });
  }

  uploadProductImage(productId: number, file: File) {
    const formData = new FormData();
    formData.append('image', file, file.name);
    return this.httpClient.post(APIRoutes.UPLOAD_PRODUCT_IMAGE + productId + '/image', formData);
  }

  getAllServiceTypes() {
    return this.httpClient.get(APIRoutes.GET_ALL_SERVICE_TYPES);
  }

  /** AddOns are excluded unless includeAddOns is true. */
  getAllProductsByTenantId(tenantId: number, includeAddOns: boolean = false) {
    const url = APIRoutes.GET_ALL_PRODUCTS_BY_TENANTID + tenantId + (includeAddOns ? '?includeAddOns=true' : '');
    return this.httpClient.get(url);
  //   return of([
  //     {
  //         'id': 3,
  //         'productName': 'SS 8 am to 1 pm',
  //         'price': 50,
  //         'description': 'asdfdsasdf',
  //         'durationPerSlot': 60,
  //         'capacityPerVenue': 1,
  //         'productType': 'Single Session',
  //         'tenantId': 2,
  //         'status': 'Inactive',
  //         'createdAt': 1663400123000,
  //         'updatedAt': 1663400545000
  //     },
  //     {
  //         'id': 4,
  //         'productName': 'SS 8pm to 1 pm',
  //         'price': 50,
  //         'description': 'sdfasdfsdf',
  //         'durationPerSlot': 60,
  //         'capacityPerVenue': 1,
  //         'productType': 'Single Session',
  //         'tenantId': 2,
  //         'status': 'Active',
  //         'createdAt': 1663400443000,
  //         'updatedAt': 1663400443000
  //     }
  // ]);
  }

  initiateTransaction(arrayJSon) {
    let jsonObject = JSON.parse(arrayJSon);
    return this.httpClient.post(APIRoutes.INITIATE_TRANSACTION, jsonObject);
  }

  //PhonePe
  initiatePayApi(arrayJSon) {
    let jsonObject = JSON.parse(arrayJSon);
    return this.httpClient.post(APIRoutes.INITIATE_PHONEPAY_PAY_API, jsonObject);
  }

  //BillDesk
  initiateBillDeskPayment(request) {
    return this.httpClient.post(APIRoutes.INITIATE_BILLDESK_PAYMENT, request);
  }

    //BillDesk
  initiateBillDeskPaymentByBookingId(bookingId, request) {
    return this.httpClient.post(APIRoutes.INITIATE_BILLDESK_PAYMENT_LINK + bookingId,request);
  }

  terminateBillDeskTransaction(terminateStatus, orderId) {
    const input = {
      terminal_state: terminateStatus,
      orderid: orderId
    };
    const formData = new URLSearchParams(input).toString();
    // Set the headers
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.httpClient.post(APIRoutes.UPDATE_BILLDESK_PAYMENT_STATUS, formData, {headers});
  }

  getBookingByConfirmationId(confirmationId) {
    return this.httpClient.get(APIRoutes.GET_BOOKING_BY_ID + confirmationId);
  }

  confirmBooking(arrayJSon, generatePaymentLink: boolean = false, expiryDuration: number = -1, cashPaymentStatus: string = null) {
    // let auth_token = sessionStorage.getItem(AppConstants.SESSION_STORAGE_ACCESS_TOKEN);
    //console.log('Auth_Token', auth_token);

    //console.log("ArrayJSON in AUth ", arrayJSon);
    // DO NOT REMOVE THIS WAS USED FOR PAYTM
    // let jsonObject = JSON.parse(arrayJSon);
    let url = APIRoutes.CONFIRM_BOOKING + "?generatePaymentLink=" + generatePaymentLink + "&expiryDuration=" + expiryDuration;
    // Cash bookings can be created as "Paid" (Booked) or "WaitingForPayment".
    if (cashPaymentStatus) {
      url += "&cashPaymentStatus=" + encodeURIComponent(cashPaymentStatus);
    }
    return this.httpClient.post(url, arrayJSon);
  }

  confirmBookingByConfirmationId(confirmationId) {
    return this.httpClient.post(APIRoutes.CONFIRM_BOOKING, {
      confirmationId: confirmationId
    });
  }

  cancelBooking(bookingID) {

    return this.httpClient.put(APIRoutes.CANCEL_BOOKING + bookingID,null);

  }

  markBookingAsPaid(bookingID) {
    return this.httpClient.put(APIRoutes.MARK_BOOKING_PAID + bookingID, null);
  }

  cancelSlots(bookingSlots, bookingId){
    return this.httpClient.put(APIRoutes.CANCEL_SLOT  + "?bookingId=" + bookingId , {
      slotsIds : bookingSlots
    });
  }

  createSession(session) {
    return this.httpClient.post(APIRoutes.CREATE_SESSION,
      {
        tenantId: session.tenantId,
        productId: session.productId,
        startTime: session.startTime,
        endTime: session.endTime,
        monday: session.monday,
        tuesday: session.tuesday,
        wednesday: session.wednesday,
        thursday: session.thursday,
        friday: session.friday,
        saturday: session.saturday,
        sunday: session.sunday
      });
  }

  deleteSessionById(sessionId : number) {
    return this.httpClient.delete(APIRoutes.DELETE_SESSION + sessionId);
  }

  createTimeSlotWiseProductsByProductId(productId) {
    return this.httpClient.post(APIRoutes.CREATE_TIME_SLOT_WISE_PRODUCTS_BY_PRODUCTID + productId, null);
  }

  createTimeSlotWiseProducts(timeslotWiseProducts) {
    const arrayOftimeslotWiseProducts: Array<any> = timeslotWiseProducts;
    return this.httpClient.post(APIRoutes.CREATE_TIME_SLOT_WISE_PRODUCTS, arrayOftimeslotWiseProducts);
  }

  updateTimeSlotWiseProducts(timeslotWiseProducts) {
    const arrayOftimeslotWiseProducts: Array<any> = timeslotWiseProducts;
    return this.httpClient.put(APIRoutes.CREATE_TIME_SLOT_WISE_PRODUCTS, arrayOftimeslotWiseProducts);
  }

  createTenantImages(tenantId: number, defaultImageIndex: number, image) {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("tenantId", String(tenantId));
    formData.append("defaultImageIndex", String(defaultImageIndex));
    return this.httpClient.post(APIRoutes.CREATE_TENANT_IMAGES, formData);
  }

  getTenantImagesByTenantId(tenantId: number) {
    return this.httpClient.get(APIRoutes.GET_TENANT_IMAGES_BY_TENANTID + tenantId, {observe: 'response'});
  }

  deleteTenantImages(tenantId: number, fileName: String, imageResponse) {
    return this.httpClient.delete(APIRoutes.GET_TENANT_IMAGES_BY_TENANTID + tenantId + "/" + fileName, { body: imageResponse });
  }

  updateTenantImagesByTenantId(tenantId: number, defaultImageIndex: number, image) {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("defaultImageIndex", String(defaultImageIndex));
    return this.httpClient.put(APIRoutes.UPDATE_TENANT_IMAGES_BY_TENANTID + tenantId, formData);
  }

  updateTenantImagesByTenantIdWithNoImage(tenantId: number, defaultImageIndex: number) {
    const formData = new FormData();
    formData.append("defaultImageIndex", String(defaultImageIndex));
    return this.httpClient.put(APIRoutes.UPDATE_TENANT_IMAGES_BY_TENANTID + tenantId, formData);
  }

  createVenue(createVenueRequest) {
    return this.httpClient.post(APIRoutes.CREATE_VENUE,
      {
        // venueType: createVenueRequest.venueType,
        venueName: createVenueRequest.venueName,
        productIds: createVenueRequest.productIds,
        tenantId: createVenueRequest.tenantId,
        repeatBookingAllowed: createVenueRequest.repeatBookingAllowed,
        status: createVenueRequest.status
      }
      );
  }

  updateVenue(id, updateVenueRequest) {
    return this.httpClient.put(APIRoutes.UPDATE_VENUE + id,
      {
        venueName: updateVenueRequest.venueName,
        productIds: updateVenueRequest.productIds,
        repeatBookingAllowed: updateVenueRequest.repeatBookingAllowed,
        status: updateVenueRequest.status
      });
  }

  getAllVenuesByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.GET_ALL_VENUES_BY_TENANTID + tenantId);

  //   return of([
  //     {
  //         'id': 4,
  //         'venueName': 'Room 1',
  //         'productIds': [
  //             3,
  //             4
  //         ],
  //         'tenantId': 2,
  //         'repeatBookingAllowed': false,
  //         'status': 'Active',
  //         'createdAt': '2022-09-17T07:35:40.000+0000',
  //         'updatedAt': '2022-09-17T07:41:04.000+0000',
  //         'venueType': ''
  //     },
  //     {
  //         'id': 5,
  //         'venueName': 'Room 2',
  //         'productIds': [
  //             3,
  //             4
  //         ],
  //         'tenantId': 2,
  //         'repeatBookingAllowed': false,
  //         'status': 'Active',
  //         'createdAt': '2022-09-17T07:36:01.000+0000',
  //         'updatedAt': '2022-09-17T07:41:19.000+0000',
  //         'venueType': ''
  //     }
  // ]);
  }
  getTenantFeeById(tenantId)  {
    return this.httpClient.get(APIRoutes.GET_TENANT_FEES + '/' + tenantId, {});
  }

  getAllAmenitiesSauByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.TENANT_AMENITIES + '/' + tenantId, {});
  }

  getAllProductsImagesSauByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.TENANT_IMAGES + '/' + tenantId);
  }

  getAlltenants() {
    return this.httpClient.get(APIRoutes.ROUTING_MODULE_PRODUCT_ALL);
  }

  getTenantFee(tenantId: any) {
    return this.httpClient.get(`/book-now/v1/tenant/fee/${tenantId}`);
  }

  getTenantServiceIcons(tenantId: number) {
    return this.getAllProductsByTenantId(tenantId)
      .pipe(
        map((products: any[]) => {

          const icons = new Set<string>();
          let hasCoaching = false;

          products.forEach(product => {

            if (product.status !== 'Active') {
              return;
            }

            if (product.serviceType) {
              icons.add(
                `../../assets/img/${product.serviceType}`
              );
            }

            if (product.isCoaching) {
              hasCoaching = true;
            }
          });

          const result = Array.from(icons);

          if (hasCoaching) {
            result.push('../../assets/img/coach.svg');
          }

          return result;
        })
      );
  }


  saveTenantFee(feeData: any) {
    return this.httpClient.post(`/book-now/v1/tenant/fee`, feeData);
  }

  getAllUsers() {
      return this.httpClient.get(APIRoutes.GET_ALL_USERS);
  }
  getUserDetails(userEmail) {
      return this.httpClient.get(APIRoutes.GET_USER + '/' + userEmail);
  }
  updateUserDetails(userParams, userId) {
      return this.httpClient.put(APIRoutes.UPDATE_USER + '/' + userId, userParams);
  }

  getConfig(config) {
    return this.httpClient.get(APIRoutes.GET_CONFIG + config);
  }

  updateConfig(config) {
    return this.httpClient.put(APIRoutes.UPDATE_CONFIG, config);
  }

  submitServiceEnquiry(payload: any) {
    return this.httpClient.post(
      APIRoutes.SERVICE_ENQUIRY,
      payload
    );
  }

  getAllTransactions(tenantId, startDate, endDate, roleId) {
    let userName = this.getUsername();
    console.log("Auth Transactions: userName  ", userName);
    console.log("Auth Transactions: tenant ", tenantId);
    console.log("Auth Transactions: start Date ", startDate);
    console.log("Auth Transactions end Date: ", endDate);
    let params = new HttpParams();
    //params = params.append('productId', newThisProdId);
    if (startDate != null && endDate != null) {
      if (startDate) {
        params = params.append('startDate', startDate);
      }
      if (endDate) {
        params = params.append('endDate', endDate);
      }
    }
    console.log("Auth Transactions params: ", params);
    params = params.append('pageNumber', 0);
    params = params.append('pageSize', 10);

    if (roleId == 1 || roleId == 2) {
      return this.httpClient.get(APIRoutes.GET_ALL_TRANSACTIONS + '/' + tenantId, { params: params });
    } else if (roleId == 3) {
      return this.httpClient.get(APIRoutes.GET_USER_TRANSACTIONS + '/' + userName);
    }
  }

  getTransactionsInPagination(tenantId, startDate, endDate, roleId, pageNumber, pageSize, sortColumn, sortDirection, searchText) {
    let userName = this.getUsername();
    console.log("Auth Transactions: userName  ", userName);
    console.log("Auth Transactions: tenant ", tenantId);
    console.log("Auth Transactions: start Date ", startDate);
    console.log("Auth Transactions end Date: ", endDate);
    let params = new HttpParams();
    //params = params.append('productId', newThisProdId);
    if (startDate != null && endDate != null) {
      if (startDate) {
        params = params.append('startDate', startDate);
      }
      if (endDate) {
        params = params.append('endDate', endDate);
      }
    }
    console.log("Auth Transactions params: ", params);
    params = params.append('pageNumber', pageNumber);
    params = params.append('pageSize', pageSize);

    if(sortColumn) {
      params = params.append('sortColumn', sortColumn);
      params = params.append('sortOrder', sortDirection);
    }

    if(searchText){
      params = params.append('userName', searchText);
    }

    if (roleId == 1 || roleId == 2) {
      return this.httpClient.get(APIRoutes.GET_ALL_TRANSACTIONS + '/' + tenantId, { params: params });
    } else if (roleId == 3) {
      return this.httpClient.get(APIRoutes.GET_USER_TRANSACTIONS + '/' + userName);
    }
  }

  getDashboardRevenue(tenantId: number, period: 'monthly' | 'daily') {
    return this.httpClient.get(`${APIRoutes.GET_DASHBOARD_REVENUE}/${tenantId}/${period}`);
  }

  getDashboardWorklists(tenantId: number) {
    return this.httpClient.get(`${APIRoutes.GET_DASHBOARD_WORKLISTS}/${tenantId}`);
  }

  fetchReport(tenantId,body)
  {
    return this.httpClient.post(APIRoutes.GET_REPORT + '/' + tenantId,
          {
            startDate: body.startDate,
            endDate: body.endDate,
            productIds: body.productIds,
            venueIds: body.venueIds
          }
          );
  }

  getSauByTenantId(tenantId) {
    return this.httpClient.get(APIRoutes.ROUTING_MODULE_PRODUCT_SINGLE + '/' + tenantId);
  }

  singleBookingRequestToServer(singleObj, tenantId) {

    let auth_token = this.getStoredItem(AppConstants.SESSION_STORAGE_ACCESS_TOKEN);
    console.log('Auth_Token', auth_token);

    const headers = new Headers({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${auth_token}`
    });

    // var date = new Date();
    // let latest_date = this.datePipe.transform(date, 'yyyy-MM-dd');

    // const formData = new FormData();
    // formData.append("startTime", String(singleObj.startTime));
    // formData.append("endTime", String(singleObj.endTime));
    // formData.append("bookingDate", latest_date);
    // formData.append("amount", singleObj.amount);
    // formData.append("category", String(singleObj.startTime));
    // formData.append("tenantId", singleObj.tenantId);
    // formData.append("venueId", singleObj.venueId);
    // formData.append("userId", singleObj.userId);
    // formData.append("productId", singleObj.productId);
    // formData.append("advanceProductId", singleObj.advanceProductId);
    // formData.append("paymentType", String(singleObj.paymentType));
    console.log("Single Obj", singleObj);
    return this.httpClient.post(APIRoutes.SINGLE_BOOKING_REQUEST, singleObj);
  }

  //getSlotsSauByTenantId(tenant, selectedService1, latest_date1, thisProdId) {
  getSlotsSauByTenantId(tenant, latest_date1, thisProdId, serviceType) {
    let auth_token = this.getStoredItem(AppConstants.SESSION_STORAGE_ACCESS_TOKEN);
    console.log('Auth_Token', auth_token);
    const headers = new Headers({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${auth_token}`
    });

    if(thisProdId == null){
      var newThisProdId = tenant;
    } else {
      var newThisProdId = thisProdId;
    }

    let params = new HttpParams();
    params = params.append('productId', newThisProdId);
    //params = params.append('venueType', selectedService1);
    params = params.append('date', latest_date1);

    console.log("Found Out X", serviceType);
    console.log("params", params);
    if(serviceType == "Single Session")
    {
      console.log("In Single", APIRoutes.ALL_SLOTS_OF_TENANT_MULTI + '/' + tenant, {params: params});
      return this.httpClient.get(APIRoutes.ALL_SLOTS_OF_TENANT_SINGLE + '/' + tenant, {params: params});
    }
    else if(serviceType == "Multi Session")
    {
      console.log("In Multi: ", APIRoutes.ALL_SLOTS_OF_TENANT_MULTI + '/' + tenant, {params: params});
      return this.httpClient.get(APIRoutes.ALL_SLOTS_OF_TENANT_MULTI + '/' + tenant, {params: params});
    }

  }

}
